<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRegistry, useLocalization } from '@hantera/portal-app'
import { TextInput, useClose } from '@hantera/design-system'

const t = useLocalization('settings.channels')
const registry = useRegistry()
const close = useClose()

const props = defineProps<{
  existingKeys: string[]
  onCreated: (key: string) => void
}>()

const KEY_PATTERN = /^[a-z_][a-z0-9_]*$/i

const key = ref('')
const label = ref('')
const saving = ref(false)

const keyError = computed(() => {
  if (!key.value) return ''
  if (!KEY_PATTERN.test(key.value)) return t('create.keyInvalid')
  if (props.existingKeys.includes(key.value)) return t('create.keyExists')
  return ''
})

const canSave = computed(() => {
  return key.value.length > 0 && label.value.trim().length > 0 && !keyError.value && !saving.value
})

async function save() {
  if (!canSave.value) return

  saving.value = true
  try {
    await registry.set(`channels/${key.value}`, { label: label.value.trim() })
    props.onCreated(key.value)
    close()
  } catch (e) {
    console.error('Failed to create channel:', e)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <fieldset class="create-dialog">
    <div>
      <label>{{ t('create.keyLabel') }}</label>
      <TextInput
        v-model="key"
        :placeholder="t('create.keyPlaceholder')"
        @keyup.enter="save"
      />
      <p class="field-help" v-if="!keyError">{{ t('create.keyHelp') }}</p>
      <p class="form__error" v-else>{{ keyError }}</p>
    </div>

    <div>
      <label>{{ t('create.labelLabel') }}</label>
      <TextInput
        v-model="label"
        :placeholder="t('create.labelPlaceholder')"
        @keyup.enter="save"
      />
    </div>

    <div class="create-dialog__actions">
      <button class="btn-primary" :disabled="!canSave" @click="save">
        {{ t('create.title') }}
      </button>
    </div>
  </fieldset>
</template>

<style scoped>
.create-dialog {
  padding: 16px;
  min-width: 400px;
}

.create-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
